package twist.mena.sports.admin.screens.notifications

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminNotificationSettingsApi
import twist.mena.sports.admin.components.Column
import twist.mena.sports.admin.components.DataTable
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.AdminNotificationDisplayItem
import twist.mena.sports.shared.dto.admin.AdminNotificationEventType
import twist.mena.sports.shared.dto.admin.AdminNotificationGroup
import twist.mena.sports.shared.dto.admin.AdminNotificationSettingsResponse

/**
 * The notification catalog: the 4 user toggles, the display items listed under them in
 * the app, and the vendor event types with their texts. Event codes the vendor sent that
 * are not mapped yet are listed first.
 */
@Composable
fun SettingsTab(api: AdminNotificationSettingsApi) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var state by remember { mutableStateOf<UiState<AdminNotificationSettingsResponse>>(UiState.Loading) }
    var editingGroup by remember { mutableStateOf<AdminNotificationGroup?>(null) }
    var editingItem by remember { mutableStateOf<AdminNotificationDisplayItem?>(null) }
    var creatingItem by remember { mutableStateOf(false) }
    var editingEvent by remember { mutableStateOf<AdminNotificationEventType?>(null) }
    var creatingEvent by remember { mutableStateOf(false) }
    // Settings writes need the admin role on the server; viewers and editors get a read-only tab.
    val canEdit = AuthState.getRole()?.let { it in listOf("scora_admin", "scora_super_admin") } ?: false

    fun load() {
        scope.launch {
            state = try {
                UiState.Success(api.getSettings())
            } catch (e: Exception) {
                UiState.Error(e.message ?: "Failed to load notification settings")
            }
        }
    }

    LaunchedEffect(Unit) { load() }

    when (val s = state) {
        is UiState.Loading -> Div({ style { color(Color(colors.textMuted)) } }) { Text("Loading…") }
        is UiState.Error -> Div({ style { errorMessageStyle(colors) } }) { Text(s.message) }
        is UiState.Success -> {
            val settings = s.data
            val unmapped = settings.eventTypes.count { it.status == "unmapped" }

            if (!canEdit) {
                Div({ style { color(Color(colors.textMuted)); fontSize(Typography.sm); marginBottom(Spacing.md) } }) {
                    Text("Read only. Changing notification settings needs the admin role.")
                }
            }
            if (unmapped > 0) {
                Div({
                    style {
                        cardStyle(colors); marginBottom(Spacing.lg)
                        property("border-left", "4px solid ${colors.warning}")
                    }
                }) {
                    Text("$unmapped event code(s) from the vendor are not mapped to a toggle yet. They are recorded in History but not sent.")
                }
            }

            // ── Toggles ──
            SectionTitle("User toggles", "The 4 switches users see. The code is part of the topic name and cannot change.")
            DataTable(
                columns = listOf<Column<AdminNotificationGroup>>(
                    Column("Code") { Code(it.code) },
                    Column("Name") { BilingualText(it.nameEn, it.nameAr) },
                    Column("Description") { BilingualText(it.descriptionEn.orEmpty(), it.descriptionAr.orEmpty(), muted = true) },
                    Column("Order") { Text(it.sortOrder.toString()) },
                    Column("Default") { OnOff(it.defaultEnabled, "On", "Off") },
                    Column("Active") { OnOff(it.active, "Active", "Hidden") },
                    Column("") { group -> if (canEdit) EditButton { editingGroup = group } }
                ),
                rows = settings.groups
            )

            // ── Display items ──
            SectionHeaderRow(
                "Display items", "Names listed under each toggle in the app. They do not affect delivery.",
                if (canEdit) "Add item" else null
            ) { creatingItem = true }
            DataTable(
                columns = listOf<Column<AdminNotificationDisplayItem>>(
                    Column("Toggle") { Code(it.groupCode) },
                    Column("Code") { Code(it.code) },
                    Column("Name") { BilingualText(it.nameEn, it.nameAr) },
                    Column("Description") { BilingualText(it.descriptionEn.orEmpty(), it.descriptionAr.orEmpty(), muted = true) },
                    Column("Order") { Text(it.sortOrder.toString()) },
                    Column("Active") { OnOff(it.active, "Active", "Hidden") },
                    Column("") { item -> if (canEdit) EditButton { editingItem = item } }
                ),
                rows = settings.displayItems,
                limit = settings.displayItems.size.coerceAtLeast(1)
            )

            // ── Event types ──
            SectionHeaderRow(
                "Event types", "Codes the vendor sends. Each maps to one toggle and carries its own texts.",
                if (canEdit) "Add event" else null
            ) { creatingEvent = true }
            DataTable(
                columns = listOf<Column<AdminNotificationEventType>>(
                    Column("Code") { event ->
                        Code(event.code)
                        if (event.status == "unmapped") {
                            Span({ style { badgeStyle(colors.warning, "#ffffff"); marginLeft(Spacing.sm) } }) { Text("unmapped") }
                        }
                    },
                    Column("Toggle / item") { event ->
                        Div { Text(event.groupCode ?: "—") }
                        event.displayItemCode?.let { item ->
                            Div({ style { color(Color(colors.textMuted)); fontSize(Typography.xs) } }) { Text(item) }
                        }
                    },
                    Column("Title") { BilingualText(it.titleTemplateEn.orEmpty(), it.titleTemplateAr.orEmpty()) },
                    Column("Body") { BilingualText(it.bodyTemplateEn.orEmpty(), it.bodyTemplateAr.orEmpty(), muted = true) },
                    Column("Image") { Text(if (it.hasImage) "Yes" else "No") },
                    Column("Valid for") { Text(formatTtl(it.ttlSeconds)) },
                    Column("Last seen (Cairo)") { Text(it.lastSeenAtUtc?.let(::cairoTime) ?: "never") },
                    Column("Enabled") { OnOff(it.enabled, "Enabled", "Off") },
                    Column("") { event -> if (canEdit) EditButton { editingEvent = event } }
                ),
                rows = settings.eventTypes,
                limit = settings.eventTypes.size.coerceAtLeast(1)
            )

            editingGroup?.let { group ->
                GroupDialog(group, api, onSaved = { load() }, onClose = { editingGroup = null })
            }
            if (creatingItem || editingItem != null) {
                DisplayItemDialog(editingItem, settings.groups, api, onSaved = { load() }) {
                    creatingItem = false; editingItem = null
                }
            }
            if (creatingEvent || editingEvent != null) {
                EventTypeDialog(editingEvent, settings.groups, settings.displayItems, api, onSaved = { load() }) {
                    creatingEvent = false; editingEvent = null
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(title: String, hint: String) {
    val colors = AppTheme.colors
    Div({ style { marginTop(Spacing.xl); marginBottom(Spacing.sm) } }) {
        Div({ style { fontSize(Typography.lg); fontWeight("600"); color(Color(colors.text)) } }) { Text(title) }
        Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) { Text(hint) }
    }
}

@Composable
private fun SectionHeaderRow(title: String, hint: String, actionLabel: String?, onAction: () -> Unit) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.FlexEnd)
        }
    }) {
        SectionTitle(title, hint)
        if (actionLabel != null) {
            Button({
                onClick { onAction() }
                style { primaryButtonStyle(colors); padding(Spacing.sm, Spacing.md); marginBottom(Spacing.sm) }
            }) { Text(actionLabel) }
        }
    }
}

@Composable
private fun Code(text: String) {
    Span({ style { fontFamily("monospace"); fontSize(Typography.sm) } }) { Text(text) }
}

@Composable
private fun BilingualText(english: String, arabic: String, muted: Boolean = false) {
    val colors = AppTheme.colors
    val textColor = if (muted) colors.textMuted else colors.text
    Div({ style { color(Color(textColor)); fontSize(Typography.sm) } }) { Text(english.ifBlank { "—" }) }
    if (arabic.isNotBlank()) {
        Div({ style { color(Color(textColor)); fontSize(Typography.sm); property("unicode-bidi", "plaintext") } }) {
            Text(arabic)
        }
    }
}

@Composable
private fun OnOff(on: Boolean, onLabel: String, offLabel: String) {
    val colors = AppTheme.colors
    Span({ style { color(Color(if (on) colors.success else colors.textMuted)); fontWeight("600") } }) {
        Text(if (on) onLabel else offLabel)
    }
}

@Composable
private fun EditButton(onClick: () -> Unit) {
    val colors = AppTheme.colors
    Button({
        onClick { onClick() }
        style { secondaryButtonStyle(colors); padding(Spacing.xs, Spacing.md); fontSize(Typography.sm) }
    }) { Text("Edit") }
}

private fun formatTtl(seconds: Int): String = when {
    seconds % 3600 == 0 -> "${seconds / 3600} h"
    seconds % 60 == 0 -> "${seconds / 60} min"
    else -> "$seconds s"
}
